import { DEFAULT_SYSTEM_SETTING_TAB_VIEWS } from '@product-framework/pages/ServiceProvider/constants';
import { useDataProvider } from '@ui-framework/index';
import { createContext, useCallback, useContext, useMemo } from 'react';
import buildSkillsConfig from './buildSkillsConfig';
import {
  getApiEndPoints,
  buildTabNavigationPath,
  deriveServiceProviderScope,
  resolveSystemSettingsTabViews,
} from './utils';
import { SCOPES } from './constants';

const ServiceProviderScopeContext = createContext(null);

function ServiceProviderScopeProvider({
  children,
  serviceProviderId,
  serviceProviderLocationId,
  mainTabIndex,
}) {
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl } = useDataProvider(provider);

  const { scope, locationId } = useMemo(
    () =>
      deriveServiceProviderScope({
        serviceProviderLocationId,
      }),
    [serviceProviderLocationId],
  );

  const apiBaseUrl = getApiUrl();

  const skillsConfig = useMemo(
    () =>
      buildSkillsConfig({
        apiBaseUrl,
        serviceProviderId,
        locationId,
      }),
    [apiBaseUrl, serviceProviderId, scope, locationId],
  );

  const systemSettingsTabViews = useMemo(
    () => resolveSystemSettingsTabViews(scope),
    [scope],
  );

  const getTabNavigationPath = useCallback(
    globalIndex =>
      buildTabNavigationPath({
        serviceProviderId,
        mainTabIndex,
        locationId,
        globalIndex,
      }),
    [serviceProviderId, mainTabIndex, locationId],
  );

  const endpoints = useMemo(
    () => getApiEndPoints(apiBaseUrl, serviceProviderId, locationId),
    [apiBaseUrl, serviceProviderId, locationId],
  );

  const value = useMemo(
    () => ({
      serviceProviderId,
      scope,
      locationId,
      mainTabIndex,
      skillsConfig,
      systemSettingsTabViews,
      getTabNavigationPath,
      endpoints,
    }),
    [
      serviceProviderId,
      scope,
      locationId,
      mainTabIndex,
      skillsConfig,
      systemSettingsTabViews,
      getTabNavigationPath,
      endpoints,
    ],
  );

  return (
    <ServiceProviderScopeContext.Provider value={value}>
      {children}
    </ServiceProviderScopeContext.Provider>
  );
}

const defaultScopeValue = {
  serviceProviderId: null,
  scope: SCOPES.SERVICE_PROVIDER,
  locationId: null,
  mainTabIndex: 0,
  skillsConfig: null,
  systemSettingsTabViews: DEFAULT_SYSTEM_SETTING_TAB_VIEWS,
  getTabNavigationPath: () => null,
  endpoints: null,
  isLocationScope: () => false,
};

function useServiceProviderScope() {
  const ctx = useContext(ServiceProviderScopeContext);
  return useMemo(
    () =>
      ctx
        ? {
            ...ctx,
            isLocationScope: ctx?.scope === SCOPES.LOCATION,
          }
        : defaultScopeValue,
    [ctx],
  );
}

export { ServiceProviderScopeProvider, useServiceProviderScope };
